import React, { FC, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { environment } from '../../../environments/environment';

interface imgProps {
  src: string;
  updatedSrc: string | null;
  alt: string;
  isShadow: boolean;
  shape: string;
  round: string;
  width: string;
  height: number;
  verticalAlignment: string;
  horizontalAlignment: string;
  isLinkActive: boolean;
  linkUrl: string;
  linkAnalyticEvent: string;
  isOpenLinkInNewTab: boolean;
  cropData: any;
  isAnchorActive: boolean;
  anchorName: string;
  anchorTrigger: string;
  selectedLinkType: string | null;
  name: string;
  editorPlugins: Record<string, any>;
}

interface imageWrapperProps {
  verticalAlignment: string;
  horizontalAlignment: string;
}

interface imageLayoutProps {
  width: string;
  round: string;
  shape: string;
  height: number;
  isShadow: boolean;
  isEditor: boolean;
}

interface imageElementProps {
  round: string;
  shape: string;
}

const ImageWrapper = styled.div<imageWrapperProps>`
  &.imageWrapper {
    position: relative;
    align-items: ${(props) => props.verticalAlignment || 'inherit'};
    justify-content: ${(props) => props.horizontalAlignment || 'inherit'};
    display: flex;
    @media (max-width: 768px) {
      align-items: center;
      justify-content: center;
    }
  }
`;

const ImageLayout = styled.div<imageLayoutProps>`
  display: flex;
  &.imageLayout {
    width: ${(props) => props.width + '%' || '100%'};
    max-width: 100%;
    border-radius: ${(props) => (props.round ? props.round : '0')};
    box-shadow: ${(props) => (props.isShadow ? 'rgba(0, 0, 0, 0.15) 0px 4px 8px' : 'none')};
    ${(props) =>
      props.shape === 'circle' &&
      `
        border-radius: 100% !important;
    `}
    img {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      ${(props) => {
        if (props.shape === 'free' || !props.shape)
          return `
                position: static;
                object-fit: contain; 
            `;
      }}
    }
    .imageWrapperInner {
      position: relative;
      overflow: hidden;
      width: 100%;
      ${(props) =>
        props.shape === 'circle' &&
        `
            border-radius: 100% !important;
            padding-top: 100%;
        `};
      ${(props) =>
        props.shape === 'square' &&
        `
            padding-top: 100%;
        `};
      ${(props) =>
        props.shape === '16-9' &&
        `
            padding-top: 56.25%;
        `};
      ${(props) =>
        props.shape === 'axis-resize' &&
        `
            height: ${props.height ? props.height + 'px' : 'auto'};
            padding-top: ${props.height ? 'unset' : '56.25%'};
        `};
    }
  }
`;

const ImageLayoutLink = styled.a<imageLayoutProps>`
  display: flex;
  ${(props) => (props.isEditor ? 'transition: unset !important;' : '')}
  &.imageLayout {
    width: ${(props) => props.width + '%' || '100%'};
    max-width: 100%;
    border-radius: ${(props) => (props.round ? props.round : '0')};
    box-shadow: ${(props) => (props.isShadow ? 'rgba(0, 0, 0, 0.15) 0px 4px 8px' : 'none')};
    ${(props) =>
      props.shape === 'circle' &&
      `
        border-radius: 100% !important;
    `}
    img {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      ${(props) => {
        if (props.shape === 'free' || !props.shape)
          return `
                position: static;
            `;
      }}
    }
    .imageWrapperInner {
      position: relative;
      overflow: hidden;
      width: 100%;
      ${(props) =>
        props.shape === 'circle' &&
        `
            border-radius: 100% !important;
            padding-top: 100%;
        `};
      ${(props) =>
        props.shape === 'square' &&
        `
            padding-top: 100%;
        `};
      ${(props) =>
        props.shape === '16-9' &&
        `
            padding-top: 56.25%;
        `};
      ${(props) =>
        props.shape === 'axis-resize' &&
        `
            height: ${props.height ? props.height + 'px' : 'auto'};
            padding-top: ${props.height ? 'unset' : '56.25%'};
        `};
    }
  }
`;

const ImageElement = styled.img<imageElementProps>`
  border-radius: ${(props) => (props.round ? props.round : '0')};
  ${(props) =>
    (props.round === '100%' || props.round === '100px') &&
    `
  border-radius: 100%;
  `};
`;

const Image: FC<imgProps> = ({ ...props }): ReactElement => {
  const imgSrc =
    props.updatedSrc || props.src || environmentLink + '/assets/images/imagePlaceholder.jpg';
  const [width, setWidth] = useState<string>(props.width);
  const [height, setHeight] = useState<number>(props.height);
  const [triggerInitialHeight, setTriggerInitialHeight] = useState<boolean>(false);
  const imageContainerRef = useRef<any>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [imageContainerWidth, setImageContainerWidth] = useState<number>(0);
  const [imageContainerHeight, setImageContainerHeight] = useState<number>(0);
  const ResizeRatio = props.editorPlugins?.ResizeRatio;
  const ResizeAxis = props.editorPlugins?.ResizeAxis;
  const isEditor: boolean = !!props.editorPlugins;

  useEffect(() => {
    if (elementRef && elementRef.current) setElementDomWidth(elementRef.current.clientWidth);
    if (imageContainerRef && imageContainerRef.current) {
      setImageContainerWidth(imageContainerRef.current.clientWidth);
      setImageContainerHeight(imageContainerRef.current.clientHeight);
    }
  }, [width, height, props.shape]);

  useEffect(() => {
    setWidth(props.width);
    setHeight(props.height);
  }, [props.width, props.height]);

  useEffect(() => {
    if (isNaN(props.height) && props.shape === 'axis-resize') {
      if (!height) setHeight(imageContainerRef.current.clientHeight);
      setTriggerInitialHeight(true);
    }
  }, [props.shape]);

  const emitWidth = (width: string): void => setWidth(width);
  const emitHeight = (height: number): void => setHeight(height);
  const getImageElement = (): ReactElement => (
    <div className="imageWrapperInner">
      <ImageElement src={imgSrc} alt={props.alt} round={props.round} shape={props.shape} />
    </div>
  );

  if ((props.isLinkActive && !props.selectedLinkType) || props.selectedLinkType === 'hyperlink') {
    return (
      <ImageWrapper
        className={'imageWrapper'}
        verticalAlignment={props.verticalAlignment}
        horizontalAlignment={props.horizontalAlignment}
        data-analytics-click-event={props.linkAnalyticEvent}
        ref={elementRef}>
        {props.shape === 'axis-resize'
          ? ResizeAxis && (
              <ResizeAxis
                widthProps={{
                  width,
                  property: 'width',
                  min: 10,
                  max: 100,
                  elementDomWidth,
                  containerWidth: imageContainerWidth,
                  emitWidth,
                }}
                heightProps={{
                  height,
                  property: 'height',
                  min: 50,
                  max: 1500,
                  containerHeight: imageContainerHeight,
                  emitHeight,
                }}
                triggerInitialHeight={triggerInitialHeight}
              />
            )
          : ResizeRatio && (
              <ResizeRatio
                width={width}
                property="width"
                min={10}
                max={100}
                suffix="%"
                elementDomWidth={elementDomWidth}
                elementContainerWidth={imageContainerWidth}
                emitWidth={emitWidth}
              />
            )}
        <ImageLayoutLink
          className={'imageLayout'}
          href={props.linkUrl}
          ref={imageContainerRef}
          isEditor={isEditor}
          target={props.isOpenLinkInNewTab ? '_blank' : '_self'}
          round={props.round}
          width={width}
          isShadow={props.isShadow}
          shape={props.shape}
          data-src={imgSrc}
          height={height}>
          {getImageElement()}
        </ImageLayoutLink>
      </ImageWrapper>
    );
  } else {
    return (
      <ImageWrapper
        className={'imageWrapper'}
        verticalAlignment={props.verticalAlignment}
        horizontalAlignment={props.horizontalAlignment}
        ref={elementRef}>
        {props.shape === 'axis-resize'
          ? ResizeAxis && (
              <ResizeAxis
                widthProps={{
                  width,
                  property: 'width',
                  min: 10,
                  max: 100,
                  elementDomWidth,
                  containerWidth: imageContainerWidth,
                  emitWidth,
                }}
                heightProps={{
                  height,
                  property: 'height',
                  min: 50,
                  max: 1500,
                  containerHeight: imageContainerHeight,
                  emitHeight,
                }}
                triggerInitialHeight={triggerInitialHeight}
              />
            )
          : ResizeRatio && (
              <ResizeRatio
                width={width}
                property="width"
                min={10}
                max={100}
                suffix="%"
                elementDomWidth={elementDomWidth}
                elementContainerWidth={imageContainerWidth}
                emitWidth={emitWidth}
              />
            )}
        <ImageLayout
          className={'imageLayout'}
          round={props.round}
          width={width}
          isShadow={props.isShadow}
          isEditor={isEditor}
          height={height}
          shape={props.shape}
          data-src={imgSrc}
          ref={imageContainerRef}>
          {getImageElement()}
        </ImageLayout>
      </ImageWrapper>
    );
  }
};

//element settings
const environmentLink: string = environment().assetsLink;
const settings: any = [
  [
    //group
    {
      settingName: 'HorizontalAlignment',
    },
  ],
  [
    //group
    {
      settingName: 'AddImage',
      title: 'Image',
      isMainSettings: true,
    },
  ],
  [
    //group
    {
      settingName: 'Size',
      controls: [
        {
          controlName: 'Slider',
          title: 'Size',
          property: 'width',
          defaultValue: 70,
          suffix: '%',
          min: 10,
        },
      ],
    },
    {
      settingName: 'Cropper',
    },
    {
      settingName: 'Shape',
      dropWidth: 'big-xxl',
      controls: [
        {
          controlName: 'ImageSelect',
          property: 'shape',
          title: 'Shape',
          defaultValue: 'square',
          useValueAndTitle: true,
          grayscale: true,
          presetData: [
            {
              title: 'Default',
              icon: environmentLink + '/assets/images/Image-select/default.svg',
              value: 'free',
              width: 20,
            },
            {
              title: 'Square',
              icon: environmentLink + '/assets/images/Image-select/square.svg',
              value: 'square',
              width: 20,
            },
            {
              title: 'Circle',
              icon: environmentLink + '/assets/images/Image-select/circle.svg',
              value: 'circle',
              width: 20,
            },
            {
              title: 'Rectangle',
              icon: environmentLink + '/assets/images/Image-select/rectangle.svg',
              value: '16-9',
              width: 20,
            },
            {
              title: 'Free form',
              icon: environmentLink + '/assets/images/Image-select/free.svg',
              value: 'axis-resize',
              width: 20,
            },
          ],
        },
      ],
    },
    {
      settingName: 'BorderRadius',
    },
    {
      settingName: 'Shadow',
    },
    {
      settingName: 'Link',
    },
    {
      settingName: 'Anchor',
    },
  ],
];

//element options
const options = [
  {
    name: 'change',
    title: 'Change',
    icon: environmentLink + '/assets/images/change-image.svg',
    action: 'openMediaLibrary',
    settingTitle: 'Image',
  },
];

export default {
  Image,
  settings,
  options,
  label: 'Image',
};
